import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { useMemo } from 'react';

import { useSkillsSafe } from '../components/chat-v2/contexts/skills-context';
import { buildSkillCommands } from '../lib/skill-constants';
import { GET_SKILLS_BY_FILTERS, type NovaSkill } from '../queries/chats';

interface UseSkillCommandsOptions {
  accountUuid?: string;
  userUuid?: string;
  preferContext?: boolean;
}

export function useSkillCommands({ accountUuid, userUuid, preferContext = true }: UseSkillCommandsOptions) {
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const skillsContext = useSkillsSafe();
  const activeSkillsContext = preferContext ? skillsContext : undefined;

  const skillsQuery = useQuery(GET_SKILLS_BY_FILTERS, {
    variables: { userUuid: userUuid!, accountUuid },
    skip: !isNovaSkillsEnabled || Boolean(activeSkillsContext) || isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const skills = useMemo<NovaSkill[]>(
    () => (isNovaSkillsEnabled ? (activeSkillsContext?.skills ?? skillsQuery.data?.skills ?? []) : []),
    [activeSkillsContext, isNovaSkillsEnabled, skillsQuery.data?.skills],
  );
  const commands = useMemo(() => buildSkillCommands(skills), [skills]);
  const isSkillsLoading = isNovaSkillsEnabled ? activeSkillsContext?.isLoading || skillsQuery.loading : false;
  const skillsError = isNovaSkillsEnabled ? (activeSkillsContext?.error ?? skillsQuery.error) : undefined;

  return {
    commands,
    skills,
    isLoading: isSkillsLoading,
    error: skillsError,
  };
}
